import React from "react";
import { useContent } from "../lib/content";
import { usePersonalization } from "../lib/personalization";
import { CheckCircle2, Calendar } from "lucide-react";

function formatStageDate(iso) {
  if (!iso) return null;
  const d = new Date(String(iso).slice(0, 10) + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const diffDays = Math.round((d - now) / 86400000);
  const dateStr = d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: d.getFullYear() !== now.getFullYear() ? "numeric" : undefined });
  let relative;
  if (diffDays > 0) relative = `in ${diffDays} day${diffDays === 1 ? "" : "s"}`;
  else if (diffDays === 0) relative = "today";
  else relative = `${-diffDays} day${diffDays === -1 ? "" : "s"} ago`;
  return { dateStr, relative, isPast: diffDays < 0, isToday: diffDays === 0 };
}

export default function Process() {
  const { content } = useContent();
  const { p } = usePersonalization();
  const stages = content.timeline || [];
  const dates = p.key_dates || {};
  const isBuySide = content?.side === "buy";

  return (
    <div className="container-x" style={{ padding: "48px 0 64px" }}>
      <span className="overline">{isBuySide ? "The buying process" : "The selling process"}</span>
      <h1 className="font-serif" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 500, margin: "8px 0 12px", letterSpacing: "-0.01em" }}>
        Know what happens, every step of the way.
      </h1>
      <p style={{ color: "var(--text-muted)", maxWidth: 680, fontSize: "1.05rem" }}>
        {isBuySide
          ? "No surprises. Here\u2019s the rhythm of a smooth home purchase — from pre-approval to keys in hand."
          : "No surprises. Here\u2019s the rhythm of a well-run home sale."
        }
      </p>

      <div style={{ position: "relative", marginTop: 56, paddingLeft: 32 }} data-testid="process-timeline">
        <div style={{ position: "absolute", top: 0, bottom: 0, left: 10, width: 2, background: "var(--border)" }} />
        {stages.map((s, i) => {
          const d = formatStageDate(dates[`stage_${s.id}`]);
          const dotStyle = d?.isPast
            ? { background: "var(--primary)", border: "2px solid var(--primary)" }
            : d?.isToday
              ? { background: "#fff", border: "2px solid var(--secondary)" }
              : { background: "var(--surface)", border: "2px solid var(--primary)" };
          return (
            <div key={s.id || i} style={{ position: "relative", paddingBottom: 40 }} data-testid={`stage-${s.id || i}`}>
              <div style={{ position: "absolute", left: -28, top: 6, width: 22, height: 22, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", ...dotStyle }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: d?.isPast ? "#fff" : d?.isToday ? "var(--secondary)" : "var(--primary)" }} />
              </div>
              <span className="overline">Stage {String(i + 1).padStart(2, "0")}</span>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 4 }}>
                <h2 className="font-serif" style={{ fontSize: 28, fontWeight: 500, margin: 0, letterSpacing: "-0.01em" }}>{s.title}</h2>
                {d && (
                  <span
                    data-testid={`stage-date-${s.id}`}
                    className="badge"
                    style={{
                      background: d.isPast ? "var(--surface-alt)" : d.isToday ? "#FBECE8" : "var(--surface-alt)",
                      color: d.isPast ? "var(--text-muted)" : d.isToday ? "var(--secondary)" : "var(--primary)",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 5,
                    }}
                  >
                    <Calendar size={11} /> {d.dateStr} · {d.relative}
                  </span>
                )}
              </div>
              <p style={{ color: "var(--text-muted)", maxWidth: 640, marginTop: 8, marginBottom: 12 }}>{s.blurb}</p>
              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
                {(s.actions || []).map((a, j) => (
                  <li key={j} style={{ display: "flex", gap: 8, alignItems: "flex-start", color: "var(--text)" }}>
                    <CheckCircle2 size={16} style={{ color: "var(--primary)", marginTop: 3, flexShrink: 0 }} />
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}
